import { create } from 'zustand';
import { AuthState, AuthResponse, User } from '../types/auth';

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  USER: 'user',
};

/**
 * Reads persisted auth out of localStorage synchronously. Used both to
 * seed the store's initial state below (so isAuthenticated is already
 * correct on the very first render -- see why that matters) and by the
 * loadFromStorage() action for any other call site.
 */
function readAuthFromStorage() {
  const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  const userJSON = localStorage.getItem(STORAGE_KEYS.USER);

  let user = null;
  if (userJSON) {
    try {
      user = JSON.parse(userJSON);
    } catch (e) {
      console.error('Failed to parse user from storage', e);
    }
  }

  if (accessToken && refreshToken) {
    return { accessToken, refreshToken, user, isAuthenticated: true };
  }
  return null;
}

/**
 * Zustand Auth Store
 * Manages authentication state and persists tokens to localStorage
 */
export const authStore = create<
  AuthState & {
    // Actions
    setTokens: (tokens: AuthResponse) => void;
    setAccessToken: (token: string) => void;
    setUser: (user: User) => void;
    logout: () => void;
    loadFromStorage: () => void;
  }
>((set) => ({
  // State -- seeded synchronously from localStorage (not via a useEffect,
  // which runs one render late) so PrivateRoute never sees a false
  // isAuthenticated on the very first render of a hard refresh and bounces
  // an already-logged-in user through /login. App.tsx's own
  // loadFromStorage() effect on mount is now a harmless idempotent re-sync,
  // not the only source of truth.
  ...(readAuthFromStorage() ?? { user: null, accessToken: null, refreshToken: null, isAuthenticated: false }),
  isLoading: false,
  error: null,

  // Actions
  /**
   * Set both access and refresh tokens, mark as authenticated
   */
  setTokens: (tokens: AuthResponse) => {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh);
    set({
      accessToken: tokens.access,
      refreshToken: tokens.refresh,
      isAuthenticated: true,
      error: null,
    });
  },

  /**
   * Update only the access token (for token refresh)
   */
  setAccessToken: (token: string) => {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
    set({ accessToken: token });
  },

  /**
   * Set the current user
   */
  setUser: (user: User) => {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    set({ user });
  },

  /**
   * Clear all authentication data
   */
  logout: () => {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      error: null,
    });
  },

  /**
   * Load tokens from localStorage (on app startup) -- now mostly a no-op
   * re-sync since the store's initial state above already does this
   * synchronously; kept for any other call site and for the case where
   * localStorage changed between store creation and this call (e.g. a
   * login completed in another tab).
   */
  loadFromStorage: () => {
    const loaded = readAuthFromStorage();
    if (loaded) {
      set(loaded);
    }
  },
}));
